import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { semantic, typography, radius, zIndex } from '@/shared/theme/tokens';
import { useBreakpoint } from '@/shared/lib/useBreakpoint';

/**
 * ModalShell — shared modal container used by every modal in the app.
 *
 * Props:
 *   onClose       — called when overlay or ✕ is clicked
 *   icon          — material-symbols name
 *   iconVariant   — 'primary' (default, blue) | 'danger' (red) | 'warning'
 *   title         — heading string or node
 *   subtitle      — subheading string or node (optional)
 *   maxWidth      — CSS string, default '520px'
 *   children      — modal body content
 *   footer        — optional footer node (action buttons)
 */
export function ModalShell({
  onClose,
  icon,
  iconVariant = 'primary',
  title,
  subtitle,
  maxWidth = '520px',
  hideClose = false,
  children,
  footer,
}) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const { isMobile } = useBreakpoint();

  const iconColors = {
    primary: {
      bg:     `linear-gradient(135deg, ${semantic.infoBg}, rgba(62,181,231,0.08))`,
      border: `1px solid ${semantic.infoBorder}`,
      text:   semantic.brandNavyMid,
    },
    danger: {
      bg:     semantic.errorContainer,
      border: `1px solid ${semantic.errorBorder}`,
      text:   semantic.error,
    },
    warning: {
      bg:     semantic.warningBg,
      border: `1px solid ${semantic.warningBorder}`,
      text:   semantic.warning,
    },
  };

  const ic = iconColors[iconVariant] ?? iconColors.primary;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(15,23,42,0.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: zIndex.modal,
        padding: isMobile ? '12px' : '24px',
        fontFamily: typography.fontFamily.body,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: c.bg,
          borderRadius: radius['3xl'],
          border: `1px solid ${c.borderSubtle}`,
          boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(15,23,42,0.18)',
          padding: isMobile ? '16px' : '28px',
          width: '100%',
          maxWidth,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {icon && (
              <div style={{
                width: 40, height: 40, borderRadius: radius.lg, flexShrink: 0,
                background: ic.bg, border: ic.border,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 20, color: ic.text, fontVariationSettings: "'FILL' 1" }}
                >
                  {icon}
                </span>
              </div>
            )}
            <div>
              {title && (
                <h3 style={{
                  margin: 0,
                  fontSize: typography.fontSize['2xl'],
                  fontWeight: typography.fontWeight.bold,
                  color: c.textPrimary,
                  fontFamily: typography.fontFamily.heading,
                }}>
                  {title}
                </h3>
              )}
              {subtitle && (
                <p style={{ margin: '2px 0 0', fontSize: typography.fontSize.base, color: c.textMuted }}>
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {!hideClose && (
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '4px', color: c.textSubtle, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                minWidth: '44px', minHeight: '44px', borderRadius: radius.md,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
            </button>
          )}
        </div>

        {/* Body */}
        {children}

        {/* Footer */}
        {footer && (
          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
